import {
  createContext,
  useContext,
  useEffect,
  useState,
  startTransition,
  useCallback,
} from "react";
import { User, SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

interface AuthContextType {
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (data: { name?: string; role?: string }) => Promise<void>;
  loading: boolean;
  supabase: SupabaseClient;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const updateUser = useCallback((session: any) => {
    startTransition(() => {
      setUser(session?.user ?? null);
    });
  }, []);

  useEffect(() => {
    // Verificar sessão atual
    supabase.auth.getSession().then(({ data: { session } }) => {
      startTransition(() => {
        updateUser(session);
        setLoading(false);
      });
    });

    // Escutar mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      startTransition(() => {
        updateUser(session);
      });
    });

    return () => subscription.unsubscribe();
  }, [updateUser]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const updateProfile = async (data: { name?: string; role?: string }) => {
    if (!user) throw new Error("No user logged in");

    const { error } = await supabase.auth.updateUser({
      data: {
        ...user.user_metadata,
        ...data,
      },
    });

    if (error) throw error;

    // Atualizar o usuário local
    setUser((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        user_metadata: {
          ...prev.user_metadata,
          ...data,
        },
      };
    });
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, signIn, signOut, updateProfile, supabase }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
